import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, forkJoin, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { User } from '../models/user';
import { WorkgroupInformationModel } from '../models/workgroup-information.model';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { OrganisationApiClient } from './organisation.apiclient';
import { TranslateService } from '@ngx-translate/core';
import { LocalStorageService } from './local-storage.service';
import { TheNaviGator } from './the-navi-gator.service';
import { SessionStorageService } from './session-storage.service';
import { MicrosoftPlugins } from '../constants/settings.enum';
import { SetNormalizedEmail } from '../directives/set-normalized-email.function';
import { IdentityMyApiClient } from './api-clients/identity-my.api-client';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;
  /**
   * Replacement for Session stored user data
   */
  public _currentUser: User = new User();
  public userdataAvailable: ReplaySubject<User>;
  organisationGuid: string;
  plugins: any = [];
  authCheckSub: Subscription = new Subscription;
  lastSeenStatusPageItemId: number = 0;

  /**
   *
   * @param navigate The navi Gator that replaces Router to send you to different pages/modules
   */
  constructor(
    private http: HttpClient,
    private sessionStorageService: SessionStorageService,
    private oidcSecurityService: OidcSecurityService,
    private localStorageService: LocalStorageService,
    private organisationApiClient: OrganisationApiClient,
    private identityMyApiClient: IdentityMyApiClient,
    private translateService: TranslateService,
    private navigate: TheNaviGator) {
    this.currentUserSubject = new BehaviorSubject<User>(new User());
    this.userdataAvailable = new ReplaySubject<User>();
    this.currentUser = this.currentUserSubject.asObservable();
    this.organisationGuid = this.sessionStorageService.organisationGuid;
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }
  /**
   * Assigned Workgroups (for Existance in)
   */
  public get UserWorkgroups(): WorkgroupInformationModel[] {
    return this.myWorkgroups?.filter((workgroup: WorkgroupInformationModel) => workgroup.isMember) ?? [];
  }
  /**
   * Assigned and public workgroups (for selection)
   */
  public get MyWorkgroups(): WorkgroupInformationModel[] {
    return this.myWorkgroups ?? [];
  }
  /** Search for Name */
  public getWorkGroupName(workgroupGuid: string): string {
    return this.orgWorkgroups?.find(workgroup => workgroup.guid === workgroupGuid)?.name ?? "";
  }
  /**
   * Workgroups assigned to the user (or public for organisation)
   */
  private myWorkgroups: WorkgroupInformationModel[] = [];
  /**
   * Workgroups assigned to the organisation
   */
  private orgWorkgroups: WorkgroupInformationModel[] = [];
  //workgroups: Array<WorkgroupInformationModel> = [];

  public logout(clearSessionStorage: boolean = true) {
    this.authCheckSub.unsubscribe()
    this.oidcSecurityService.logoff().subscribe(() => {
      if (clearSessionStorage) {
        this.sessionStorageService.Clear();
      }
    });
  }

  public isAuthenticated: boolean = false;
  private authResponseIsValid: boolean = false;
  public authorizeCallback() {
    let returnUrl: string | null = this.sessionStorageService.returnUrl;
    if (returnUrl && returnUrl.indexOf('http') == 0) {
      this.navigate.To_Unauthorized(returnUrl);
      return;	// prevent open redirect
    }
    this.oidcSecurityService.checkAuth().subscribe({
      next: ({ isAuthenticated, userData }) => {
        this.isAuthenticated = isAuthenticated;
        if (isAuthenticated) { this.authenticated(userData) }
        if (this.authResponseIsValid) {
          this.authResponseValid(returnUrl)
        } else {
          this.authResponseInValid(returnUrl);
          // TODO: check for returnUrl in params, set returnurl to be the same if present
        }
      },
      error: () => {
        this.navigate.To_Unauthorized(returnUrl ?? '');
      }
    });
    this.oidcSecurityService.userData$.subscribe(data => this.getUserInfo(data));
  }

  public ResetUserInfo() {
    this.oidcSecurityService.checkAuth().subscribe(({ isAuthenticated, userData }) => {
      if (isAuthenticated) {
        this.getUserInfo(userData);
      }
    });
  }

  private authenticated(userData: any) {
    // reset properties:
    this.sessionStorageService.authNonce = "";
    this.sessionStorageService.authStateControl = "";
    this.authResponseIsValid = true;
  }

  private authResponseValid(returnUrl: string | null | undefined) {
    this.authCheckSub = this.oidcSecurityService.isAuthenticated$.subscribe((authorized) => {
      let user = this._currentUser;
      if (!authorized.isAuthenticated) {
        if (user.isCustomer == undefined || user.isCustomer == null || user.isCustomer == false) {
          this.logout();
          return;
        }
        this.authResponseInValid(document.location.pathname + document.location.search);
      }
    })
    if (returnUrl !== null && returnUrl !== undefined && returnUrl !== "undefined") {
      this.navigate.To_Url(returnUrl);
    }
    else {
      this.navigate.To_Dashboard();
    }
  }

  private authResponseInValid(returnUrl: string | null | undefined) {
    this.navigate.To_Login(returnUrl);
  }

  private getUserInfo(dataObject: any) {
    let userData: any = dataObject.userData;
    let u2: User = new User();
    if (userData) {
      u2.email = SetNormalizedEmail(userData.email);
      u2.fullName = userData.FullName;
      u2.firstname = userData.FirstName;
      u2.prefix = userData.Prefix;
      u2.lastname = userData.LastName;
      u2.role = userData.role;
      u2.phoneNumber = userData.phone_number;
      u2.subscriptionLevel = userData.SubscriptionLevel;
      u2.organisationGuid = userData.OrganisationGuid;
      u2.isCustomer = userData.role && userData.role.indexOf("Customer") > -1;
      u2.isOrganisationAdmin = userData.role && userData.role.indexOf("OrganisationAdmin") > -1;
      u2.isWhitelabelAdmin = userData.role && userData.role.indexOf("EnterpriseAdmin") > -1;
      u2.isSystemAdmin = userData.role && userData.role.indexOf("WhitelabelAdmin") > -1;
      u2.sub = userData.sub;
      u2.cscProviderName = userData.CscProviderName;
      u2.signatureProvider = userData.SignatureProvider;
      u2.language = userData.language;
      this.emitUser(u2);
      this.organisationGuid = userData.OrganisationGuid;
      this.userdataAvailable.next(u2);
      // Load additional info
      this.LoadWorkgroupsAndPlugins();
    }
  }

  /***
   * Makes additional calls and executes them in parallel to get additional user data and set it on the current user
   * -Workgroups assigned to the user
   * -Workgroups assigned to the organisation
   * -Enabled plugins for the organisation
   * -Whether a Microsoft plugin is enabled for the organisation
   */
  public LoadWorkgroupsAndPlugins() {
    // make them execute all and than only emit one change
    forkJoin({
      // MY assignable workgroups
      myWorkgroups: this.identityMyApiClient.GetMyWorkgroups().pipe(catchError(() => of(null))),
      // ALL workgroups for organisation (names/display)
      orgWorkgroups: this.identityMyApiClient.GetOrganisationWorkgroups().pipe(catchError(() => of(null))),
      //Plugins
      plugins: this.organisationApiClient.getEnabledPlugins(this.currentUserValue.organisationGuid).pipe(catchError(() => of(null)))
    }).pipe(
      finalize(() => this.emitUser(this.currentUserValue))
    ).subscribe(({ myWorkgroups, orgWorkgroups, plugins }) => {
      if (myWorkgroups) {
        this.myWorkgroups = myWorkgroups;
      }
      if (orgWorkgroups) {
        this.orgWorkgroups = orgWorkgroups;
      }
      if (plugins) {
        this.currentUserValue.enabledOrganisationPlugins = plugins;
        if (plugins.filter(s => MicrosoftPlugins.includes(s.code)).length > 0) {
          this.currentUserValue.aMsPluginIsEnabled = true;
        }
      }
    }
    );
  }

  private emitUser(user: User) {
    this.sessionStorageService.organisationGuid = user.organisationGuid;
    if (user.language != undefined && user.language != null) {
      this.localStorageService.Language = user.language;
      this.translateService.use(user.language.toLowerCase());
    }
    this.currentUserSubject.next(user);
    this._currentUser = user;
  }

  urlBase64Decode(str: string): string {
    let output: string = str.replace('-', '+').replace('_', '/');
    switch (output.length % 4) {
      case 0:
        break;
      case 2:
        output += '==';
        break;
      case 3:
        output += '=';
        break;
      default:
        throw 'Illegal base64url string!';
    }

    return window.atob(output);
  }

  public forceNewToken() {
    return this.oidcSecurityService.forceRefreshSession()
  }
}
